import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, ArrowLeft } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import axios from 'axios';
import { Button } from '../components/ui/button';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const BlogArticlePage = () => {
  const { slug } = useParams();
  const { language, t } = useLanguage();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const fetchArticle = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/blog/${slug}`);
      setArticle(response.data);
    } catch (err) { setError(true); }
    finally { setLoading(false); }
  }, [slug]);

  useEffect(() => { fetchArticle(); }, [fetchArticle]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString(language === 'pt' ? 'pt-BR' : 'en-US', { year: 'numeric', month: 'long', day: 'numeric' });
  };

  if (loading) return <div className="min-h-screen pt-24 bg-white flex items-center justify-center bg-white"><p className="text-steel">{t('Carregando...', 'Loading...')}</p></div>;
  if (error || !article) return (
    <div className="min-h-screen pt-24 bg-white flex items-center justify-center bg-white">
      <div className="text-center">
        <h1 className="text-3xl font-bold text-abyssal mb-4">{t('Artigo não encontrado', 'Article not found')}</h1>
        <Link to="/blog" data-testid="back-to-blog-link"><Button variant="outline" className="border-[#E6E6ED] text-steel"><ArrowLeft className="mr-2 h-4 w-4" />{t('Voltar ao blog', 'Back to blog')}</Button></Link>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen pt-24 bg-white">
      <section className="py-16 md:py-20 bg-white border-b border-[#E6E6ED]">
        <div className="max-w-[860px] mx-auto px-6">
          <Link to="/blog" data-testid="back-to-blog" className="inline-flex items-center text-cobalt hover:text-abyssal mb-8 text-sm font-medium">
            <ArrowLeft className="mr-2 h-4 w-4" /><span>{t('Voltar ao blog', 'Back to blog')}</span>
          </Link>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-brass"></span>
              <span className="text-[11px] font-semibold uppercase tracking-[0.28em] text-brass">{article.category}</span>
            </div>
            <h1 className="text-[36px] sm:text-5xl tracking-tight font-bold text-abyssal leading-[1.1] mb-6">{language === 'pt' ? article.title_pt : article.title_en}</h1>
            <div className="flex items-center gap-4 text-steel text-sm">
              <div className="flex items-center gap-2"><Calendar className="h-4 w-4" /><span>{formatDate(article.created_at)}</span></div>
              <span className="text-steel-400">|</span>
              <span>{article.author}</span>
            </div>
          </motion.div>
        </div>
      </section>
      <section className="py-14 md:py-20 bg-mist">
        <div className="max-w-[860px] mx-auto px-6">
          <img src={article.image_url} alt={language === 'pt' ? article.title_pt : article.title_en} className="w-full h-auto rounded-md mb-10" />
          <div className="text-lg text-abyssal leading-relaxed prose max-w-none" style={{ whiteSpace: 'pre-line' }}>
            {language === 'pt' ? article.content_pt : article.content_en}
          </div>
        </div>
      </section>
    </div>
  );
};

export default BlogArticlePage;
